import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, IconButton, Button,
  TextField, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, CircularProgress, Alert, Snackbar,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Grid, Tooltip, TablePagination,
} from '@mui/material';
import {
  Add as AddIcon, Search as SearchIcon, Edit as EditIcon, Delete as DeleteIcon,
  Refresh as RefreshIcon, CreditCard as CreditCardIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import { usePageBreakpoints, pageContainerSx } from '../../hooks/useClinicPageBreakpoints';


const formatCurrency = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

const CARD_BRANDS = [
  { value: 'visa', label: 'Visa', color: '#1a1f71' },
  { value: 'mastercard', label: 'Mastercard', color: '#eb001b' },
  { value: 'elo', label: 'Elo', color: '#000000' },
  { value: 'amex', label: 'Amex', color: '#006fcf' },
  { value: 'hipercard', label: 'Hipercard', color: '#822124' },
];

const CARD_TYPES = [
  { value: 'credit', label: 'Crédito' },
  { value: 'debit', label: 'Débito' },
];

const STATUSES = [
  { value: 'pending', label: 'Pendente', color: '#f59e0b', bgcolor: '#fef3cd' },
  { value: 'settled', label: 'Liquidado', color: '#22c55e', bgcolor: '#dcfce7' },
  { value: 'cancelled', label: 'Cancelado', color: '#ef4444', bgcolor: '#fef2f2' },
];

const brandColorMap: Record<string, { color: string; bgcolor: string }> = {
  visa: { color: '#1a1f71', bgcolor: '#e8eaf6' },
  mastercard: { color: '#eb001b', bgcolor: '#fce4ec' },
  elo: { color: '#000000', bgcolor: '#f5f5f5' },
  amex: { color: '#006fcf', bgcolor: '#e3f2fd' },
  hipercard: { color: '#822124', bgcolor: '#fbe9e7' },
};

const statusMap: Record<string, { label: string; color: string; bgcolor: string }> = {
  pending: { label: 'Pendente', color: '#f59e0b', bgcolor: '#fef3cd' },
  settled: { label: 'Liquidado', color: '#22c55e', bgcolor: '#dcfce7' },
  cancelled: { label: 'Cancelado', color: '#ef4444', bgcolor: '#fef2f2' },
};

const emptyForm = {
  terminal_id: '',
  card_brand: '',
  card_type: 'credit',
  authorization_code: '',
  nsu: '',
  amount: 0,
  installments: 1,
  fee_percentage: 0,
  fee_amount: 0,
  net_amount: 0,
  expected_settlement_date: '',
  status: 'pending',
  notes: '',
};

const TefTransactions: React.FC = () => {
  const { isMobile } = usePageBreakpoints();

  const [transactions, setTransactions] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // Filters
  const [search, setSearch] = useState('');
  const [filterBrand, setFilterBrand] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);

  // Dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const fetchTransactions = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = { page: page + 1, limit };
      if (search.trim()) params.search = search.trim();
      if (filterBrand) params.card_brand = filterBrand;
      if (filterType) params.card_type = filterType;
      if (filterStatus) params.status = filterStatus;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await axios.get(getApiUrl('/financial-module/tef'), { params });
      setTransactions(data.data || []);
      setTotalCount(data.pagination?.total || data.total || 0);
    } catch (e) {
      console.error(e);
      setSnackbar({ open: true, message: 'Erro ao carregar transações TEF', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, filterBrand, filterType, filterStatus, dateFrom, dateTo]);

  useEffect(() => { fetchTransactions(); }, [fetchTransactions]);

  const recalcFees = (amount: number, feePercentage: number) => {
    const feeAmount = (amount * feePercentage) / 100;
    const netAmount = amount - feeAmount;
    return { fee_amount: Math.round(feeAmount * 100) / 100, net_amount: Math.round(netAmount * 100) / 100 };
  };

  const handleFormChange = (field: string, value: any) => {
    const updated = { ...formData, [field]: value };
    if (field === 'amount' || field === 'fee_percentage') {
      const amt = field === 'amount' ? Number(value) : Number(updated.amount);
      const pct = field === 'fee_percentage' ? Number(value) : Number(updated.fee_percentage);
      const { fee_amount, net_amount } = recalcFees(amt, pct);
      updated.fee_amount = fee_amount;
      updated.net_amount = net_amount;
    }
    setFormData(updated);
  };

  const openCreate = () => {
    setEditingId(null);
    setFormData({ ...emptyForm });
    setDialogOpen(true);
  };

  const openEdit = (row: any) => {
    setEditingId(row.id);
    setFormData({
      terminal_id: row.terminal_id || '',
      card_brand: row.card_brand || '',
      card_type: row.card_type || 'credit',
      authorization_code: row.authorization_code || '',
      nsu: row.nsu || '',
      amount: row.amount || 0,
      installments: row.installments || 1,
      fee_percentage: row.fee_percentage || 0,
      fee_amount: row.fee_amount || 0,
      net_amount: row.net_amount || 0,
      expected_settlement_date: row.expected_settlement_date ? row.expected_settlement_date.substring(0, 10) : '',
      status: row.status || 'pending',
      notes: row.notes || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      if (editingId) {
        await axios.put(getApiUrl(`/financial-module/tef/${editingId}`), formData);
        setSnackbar({ open: true, message: 'Transação atualizada!', severity: 'success' });
      } else {
        await axios.post(getApiUrl('/financial-module/tef'), formData);
        setSnackbar({ open: true, message: 'Transação criada!', severity: 'success' });
      }
      setDialogOpen(false);
      fetchTransactions();
    } catch (e: any) {
      setSnackbar({ open: true, message: e.response?.data?.error || 'Erro ao salvar', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Excluir esta transação TEF?')) return;
    try {
      await axios.delete(getApiUrl(`/financial-module/tef/${id}`));
      setSnackbar({ open: true, message: 'Transação excluída!', severity: 'success' });
      fetchTransactions();
    } catch (e) {
      setSnackbar({ open: true, message: 'Erro ao excluir', severity: 'error' });
    }
  };

  const clearFilters = () => {
    setSearch('');
    setFilterBrand('');
    setFilterType('');
    setFilterStatus('');
    setDateFrom('');
    setDateTo('');
    setPage(0);
  };

  const hasActiveFilters = search || filterBrand || filterType || filterStatus || dateFrom || dateTo;

  return (
    <Box sx={pageContainerSx}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a' }}>Transações TEF</Typography>
          <Typography variant="body2" sx={{ color: '#64748b' }}>Gerenciamento de transações de transferência eletrônica de fundos</Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreate}
          sx={{ bgcolor: '#14B8A6', '&:hover': { bgcolor: '#0d9488' } }}
        >
          Nova Transação
        </Button>
      </Box>

      {/* Filters */}
      <Card sx={{ borderRadius: 3, mb: 3, p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth size="small" placeholder="Buscar NSU, autorização..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8' }} /></InputAdornment>,
              }}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Bandeira</InputLabel>
              <Select value={filterBrand} label="Bandeira" onChange={(e) => { setFilterBrand(e.target.value); setPage(0); }}>
                <MenuItem value="">Todas</MenuItem>
                {CARD_BRANDS.map(b => <MenuItem key={b.value} value={b.value}>{b.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Tipo</InputLabel>
              <Select value={filterType} label="Tipo" onChange={(e) => { setFilterType(e.target.value); setPage(0); }}>
                <MenuItem value="">Todos</MenuItem>
                {CARD_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select value={filterStatus} label="Status" onChange={(e) => { setFilterStatus(e.target.value); setPage(0); }}>
                <MenuItem value="">Todos</MenuItem>
                {STATUSES.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={3} md={1.5}>
            <TextField
              fullWidth size="small" type="date" label="Data Início"
              InputLabelProps={{ shrink: true }}
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setPage(0); }}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={1.5}>
            <TextField
              fullWidth size="small" type="date" label="Data Fim"
              InputLabelProps={{ shrink: true }}
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
            />
          </Grid>
          <Grid item xs={12} sm="auto" sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Atualizar">
              <IconButton onClick={fetchTransactions} sx={{ color: '#14B8A6' }}><RefreshIcon /></IconButton>
            </Tooltip>
            {hasActiveFilters && (
              <Tooltip title="Limpar filtros">
                <IconButton onClick={clearFilters} sx={{ color: '#ef4444' }}><ClearIcon /></IconButton>
              </Tooltip>
            )}
          </Grid>
        </Grid>
      </Card>

      {/* Table */}
      <Card sx={{ borderRadius: 3 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 600 }}>Terminal</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Bandeira</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Tipo</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>NSU</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Autorização</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Valor</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Taxa</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Líquido</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Parcelas</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Prev. Liquidação</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={12} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={36} sx={{ color: '#14B8A6' }} />
                  </TableCell>
                </TableRow>
              ) : transactions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={12} align="center" sx={{ py: 6, color: '#94a3b8' }}>
                    Nenhuma transação TEF encontrada
                  </TableCell>
                </TableRow>
              ) : transactions.map((row) => {
                const brand = brandColorMap[row.card_brand] || { color: '#64748b', bgcolor: '#f1f5f9' };
                const status = statusMap[row.status] || statusMap.pending;
                return (
                  <TableRow key={row.id} hover>
                    <TableCell sx={{ fontWeight: 500 }}>{row.terminal_id || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={(row.card_brand || '-').toUpperCase()}
                        size="small"
                        sx={{ bgcolor: brand.bgcolor, color: brand.color, fontWeight: 700, fontSize: '0.7rem' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={row.card_type === 'credit' ? 'Crédito' : row.card_type === 'debit' ? 'Débito' : (row.card_type || '-')}
                        size="small"
                        variant="outlined"
                        sx={{ fontWeight: 500, borderColor: '#94a3b8' }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{row.nsu || '-'}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{row.authorization_code || '-'}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{formatCurrency(row.amount)}</TableCell>
                    <TableCell align="right" sx={{ color: '#ef4444', fontSize: '0.85rem' }}>
                      {formatCurrency(row.fee_amount)} ({row.fee_percentage ?? 0}%)
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: '#14B8A6' }}>{formatCurrency(row.net_amount)}</TableCell>
                    <TableCell align="center">{row.installments || 1}x</TableCell>
                    <TableCell sx={{ fontSize: '0.85rem' }}>
                      {row.expected_settlement_date
                        ? new Date(row.expected_settlement_date).toLocaleDateString('pt-BR')
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={status.label}
                        size="small"
                        sx={{ bgcolor: status.bgcolor, color: status.color, fontWeight: 600, fontSize: '0.7rem' }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => openEdit(row)} sx={{ color: '#3b82f6' }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <IconButton size="small" onClick={() => handleDelete(row.id)} sx={{ color: '#ef4444' }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {transactions.length > 0 && (
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={limit}
            onRowsPerPageChange={(e) => { setLimit(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Linhas por página"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`}
          />
        )}
      </Card>

      {/* ========== CREATE/EDIT DIALOG ========== */}
      <Dialog fullScreen={isMobile} scroll="paper" open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>
          {editingId ? 'Editar' : 'Nova'} Transação TEF
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth size="small" label="Terminal ID"
                value={formData.terminal_id}
                onChange={(e) => handleFormChange('terminal_id', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Bandeira</InputLabel>
                <Select value={formData.card_brand} label="Bandeira" onChange={(e) => handleFormChange('card_brand', e.target.value)}>
                  {CARD_BRANDS.map(b => <MenuItem key={b.value} value={b.value}>{b.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo Cartão</InputLabel>
                <Select value={formData.card_type} label="Tipo Cartão" onChange={(e) => handleFormChange('card_type', e.target.value)}>
                  {CARD_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth size="small" label="Código de Autorização"
                value={formData.authorization_code}
                onChange={(e) => handleFormChange('authorization_code', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth size="small" label="NSU"
                value={formData.nsu}
                onChange={(e) => handleFormChange('nsu', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth size="small" label="Valor (R$)" type="number"
                value={formData.amount}
                onChange={(e) => handleFormChange('amount', e.target.value)}
                InputProps={{ inputProps: { min: 0, step: 0.01 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth size="small" label="Parcelas" type="number"
                value={formData.installments}
                onChange={(e) => handleFormChange('installments', parseInt(e.target.value, 10) || 1)}
                InputProps={{ inputProps: { min: 1, max: 48 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth size="small" label="Taxa (%)" type="number"
                value={formData.fee_percentage}
                onChange={(e) => handleFormChange('fee_percentage', e.target.value)}
                InputProps={{ inputProps: { min: 0, max: 100, step: 0.01 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth size="small" label="Valor Taxa (R$)" type="number"
                value={formData.fee_amount}
                InputProps={{ readOnly: true }}
                sx={{ '& .MuiInputBase-root': { bgcolor: '#f8fafc' } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth size="small" label="Valor Líquido (R$)" type="number"
                value={formData.net_amount}
                InputProps={{ readOnly: true }}
                sx={{ '& .MuiInputBase-root': { bgcolor: '#f8fafc' } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small" label="Previsão Liquidação" type="date"
                InputLabelProps={{ shrink: true }}
                value={formData.expected_settlement_date}
                onChange={(e) => handleFormChange('expected_settlement_date', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select value={formData.status} label="Status" onChange={(e) => handleFormChange('status', e.target.value)}>
                  {STATUSES.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth size="small" label="Observações" multiline rows={2}
                value={formData.notes}
                onChange={(e) => handleFormChange('notes', e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={18} /> : <CreditCardIcon />}
            sx={{ bgcolor: '#14B8A6', '&:hover': { bgcolor: '#0d9488' } }}
          >
            {editingId ? 'Atualizar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========== SNACKBAR ========== */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: isMobile ? 'center' : 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TefTransactions;
